import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  BadgeDollarSign,
  CheckCircle2,
  Download,
  FilterX,
  KeyRound,
  LayoutGrid,
  List,
  Plus,
  Search,
  Sparkles,
  Unplug,
} from "lucide-react";
import { toast } from "sonner";

import { IntegrationCard } from "@/components/integrations/IntegrationCard";
import { IntegrationModal } from "@/components/integrations/IntegrationModal";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Integration,
  useAddIntegration,
  useDeleteIntegration,
  useIntegrations,
  useIntegrationsFilter,
  useIntegrationsStore,
  useRotateKey,
  useSetIntegrationFilter,
  useUpdateIntegration,
} from "@/lib/integrations-store";
import { useTranslation } from "@/lib/i18n";

type StatusFilter = "all" | "connected" | "warning" | "expired";

const FILTER_TABS = [
  { key: "all", label: "Tất cả" },
  { key: "LLM", label: "LLM" },
  { key: "CRM", label: "CRM" },
  { key: "Productivity", label: "Năng suất" },
  { key: "Finance", label: "Tài chính" },
  { key: "Custom", label: "Tùy chỉnh" },
] as const;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export default function IntegrationsPage() {
  const { t } = useTranslation();
  const integrations = useIntegrations();
  const addIntegration = useAddIntegration();
  const activeFilter = useIntegrationsFilter();
  const setFilter = useSetIntegrationFilter();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isHydrated, setIsHydrated] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = useIntegrationsStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });

    if (useIntegrationsStore.persist.hasHydrated()) {
      setIsHydrated(true);
    }

    return () => unsubscribe();
  }, []);

  const filteredIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      const normalizedSearch = search.trim().toLowerCase();
      const matchesSearch =
        normalizedSearch.length === 0 ||
        integration.name.toLowerCase().includes(normalizedSearch) ||
        integration.provider.toLowerCase().includes(normalizedSearch);
      const matchesCategory = activeFilter === "all" || integration.type === activeFilter;
      const matchesStatus = statusFilter === "all" || integration.status === statusFilter;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [integrations, search, activeFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = integrations.length;
    const active = integrations.filter((item) => item.status === "connected").length;
    const monthlyCost = integrations.reduce((sum, item) => sum + item.metrics.cost, 0);
    const warningCount = integrations.filter((item) => item.status === "warning").length;
    const expiredCount = integrations.filter((item) => item.status === "expired").length;
    const issueCount = warningCount + expiredCount;

    return {
      total,
      active,
      monthlyCost,
      warningCount,
      expiredCount,
      issueCount,
    };
  }, [integrations]);

  const hasFilters = activeFilter !== "all" || statusFilter !== "all" || search.trim().length > 0;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setFilter("all");
  };

  const handleOpenImport = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw) as Integration[] | { integrations?: Integration[] };
      const items = Array.isArray(parsed) ? parsed : parsed.integrations ?? [];

      if (!Array.isArray(items) || items.length === 0) {
        toast.error("File import không chứa danh sách kết nối hợp lệ.");
        return;
      }

      let importedCount = 0;
      for (const item of items) {
        if (!item?.name || !item?.provider || !item?.type || !item?.status || !item?.metrics || !item?.authData) {
          continue;
        }

        addIntegration({
          ...item,
          id: item.id || `int_${crypto.randomUUID()}`,
        });
        importedCount += 1;
      }

      if (importedCount === 0) {
        toast.error("Không có bản ghi nào đủ dữ liệu để import.");
      } else {
        toast.success(`Đã import ${importedCount} kết nối từ file.`);
      }
    } catch {
      toast.error("Không thể đọc file import. Hãy dùng JSON hợp lệ.");
    } finally {
      event.target.value = "";
    }
  };

  return (
    <AppLayout container contentClassName="space-y-8">
      <section className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <Badge className="w-fit rounded-full border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
              Integrations Management
            </Badge>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">{t("integrations_title")}</h1>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
                Quản lý toàn bộ kết nối API, khóa truy cập và trạng thái vận hành của hệ sinh thái agent trong một bảng điều khiển duy nhất.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              variant="outline"
              className="h-11 gap-2 rounded-full border-white/50 bg-card/70 px-5 shadow-soft"
              onClick={handleOpenImport}
            >
              <Download className="h-4 w-4" /> {t("import_file")}
            </Button>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="h-11 gap-2 rounded-full bg-gradient-primary px-5 text-primary-foreground shadow-glow hover:opacity-95"
            >
              <Plus className="h-4 w-4" /> {t("create_integration")}
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            title="Tổng số kết nối"
            value={stats.total.toString()}
            description="Số kết nối đang được quản lý"
            icon={<KeyRound className="h-5 w-5 text-chart-3" />}
          />
          <SummaryCard
            title="Đang hoạt động"
            value={stats.active.toString()}
            description={`${Math.round((stats.active / Math.max(stats.total, 1)) * 100)}% đang ổn định`}
            icon={<CheckCircle2 className="h-5 w-5 text-primary" />}
          />
          <SummaryCard
            title="Monthly Cost"
            value={formatCurrency(stats.monthlyCost)}
            description={stats.monthlyCost > 250 ? "Chi phí vượt ngưỡng cần theo dõi" : "Chi phí đang trong ngưỡng an toàn"}
            icon={<BadgeDollarSign className="h-5 w-5 text-indigo-500" />}
            emphasize={stats.monthlyCost > 250 ? "warning" : "default"}
          />
          <SummaryCard
            title="Sắp hết hạn / Có lỗi"
            value={stats.issueCount.toString()}
            description={`${stats.expiredCount} expired • ${stats.warningCount} warning`}
            icon={<AlertTriangle className="h-5 w-5 text-destructive" />}
            emphasize={stats.issueCount > 0 ? "danger" : "default"}
          />
        </div>
      </section>

      <section className="sticky top-14 z-[5] rounded-[1.35rem] border border-white/40 bg-background/85 shadow-soft backdrop-blur-xl">
        <div className="flex flex-col gap-4 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Tìm theo tên kết nối hoặc provider..."
                className="h-11 rounded-full border-white/50 bg-card/65 pl-10"
                aria-label="Tìm kiếm kết nối"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <StatusChip label="Mọi trạng thái" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
              <StatusChip label="Connected" active={statusFilter === "connected"} onClick={() => setStatusFilter("connected")} />
              <StatusChip label="Warning" active={statusFilter === "warning"} tone="warning" onClick={() => setStatusFilter("warning")} />
              <StatusChip label="Expired" active={statusFilter === "expired"} tone="danger" onClick={() => setStatusFilter("expired")} />
            </div>
          </div>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setFilter(tab.key)}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                    activeFilter === tab.key
                      ? "border-primary/20 bg-primary text-primary-foreground"
                      : "border-border bg-card/60 text-foreground hover:border-primary/20 hover:bg-primary/5"
                  }`}
                  aria-pressed={activeFilter === tab.key}
                >
                  {tab.label}
                </button>
              ))}

              {hasFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="rounded-full px-3 text-muted-foreground"
                >
                  <FilterX className="mr-2 h-4 w-4" /> Xóa bộ lọc
                </Button>
              ) : null}
            </div>

            <div className="flex items-center gap-2 self-end">
              <div className="flex overflow-hidden rounded-full border border-border bg-card/70">
                <Button
                  variant={viewMode === "grid" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-10 w-10 rounded-none"
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "table" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-10 w-10 rounded-none"
                  onClick={() => setViewMode("table")}
                  aria-label="Table view"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!isHydrated ? (
        <IntegrationSkeletonGrid />
      ) : (
        <section className="space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-foreground">{filteredIntegrations.length} kết nối phù hợp</p>
              <p className="text-sm text-muted-foreground">Ưu tiên các kết nối có vấn đề để xử lý nhanh các rủi ro vận hành.</p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {filteredIntegrations.length === 0 ? (
              <motion.div
                key="empty-state"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex min-h-[360px] flex-col items-center justify-center rounded-[1.6rem] border border-dashed border-border bg-card/50 px-6 py-14 text-center"
              >
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                  {integrations.length === 0 ? <Sparkles className="h-7 w-7" /> : <Unplug className="h-7 w-7" />}
                </div>
                <h2 className="text-xl font-semibold">{integrations.length === 0 ? "Chưa có kết nối nào" : "Không tìm thấy kết nối phù hợp"}</h2>
                <p className="mt-2 max-w-lg text-sm text-muted-foreground">
                  {integrations.length === 0
                    ? "Bắt đầu bằng cách thêm kết nối đầu tiên để agent có thể truy cập dữ liệu, LLM và hệ thống nghiệp vụ."
                    : "Thử đổi từ khóa tìm kiếm hoặc xóa filter để xem lại toàn bộ danh sách kết nối."}
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    className="rounded-full bg-gradient-primary px-5 text-primary-foreground shadow-glow hover:opacity-95"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Thêm kết nối đầu tiên
                  </Button>
                  {hasFilters ? (
                    <Button variant="outline" className="rounded-full px-5" onClick={clearFilters}>
                      Xóa bộ lọc
                    </Button>
                  ) : null}
                </div>
              </motion.div>
            ) : viewMode === "table" ? (
              <motion.div
                key="table-state"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <IntegrationTable integrations={filteredIntegrations} />
              </motion.div>
            ) : (
              <motion.div
                key="grid-state"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3"
              >
                {filteredIntegrations.map((integration) => (
                  <IntegrationCard key={integration.id} integration={integration} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      )}

      <IntegrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
      <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImportFile} />
    </AppLayout>
  );
}

function IntegrationTable({ integrations }: { integrations: Integration[] }) {
  const updateIntegration = useUpdateIntegration();
  const rotateKey = useRotateKey();
  const deleteIntegration = useDeleteIntegration();

  const refreshIntegration = (integration: Integration) => {
    updateIntegration(integration.id, {
      metrics: {
        ...integration.metrics,
        lastUsed: "just now",
      },
    });
    toast.success(`Đã làm mới trạng thái của ${integration.name}.`);
  };

  const renewKey = (integration: Integration) => {
    rotateKey(integration.id);
    toast.success(`Đã tạo khóa mới cho ${integration.name}.`);
  };

  const resolveIssue = (integration: Integration) => {
    updateIntegration(integration.id, {
      status: "connected",
      metrics: {
        ...integration.metrics,
        lastUsed: "just now",
        successRate: Math.max(integration.metrics.successRate, 98),
      },
    });
    toast.success(`Đã xử lý trạng thái của ${integration.name}.`);
  };

  const disconnectIntegration = (integration: Integration) => {
    deleteIntegration(integration.id);
    toast.error(`Đã ngắt kết nối ${integration.name}.`);
  };

  return (
    <Card className="overflow-hidden rounded-[1.5rem] border border-white/35 bg-card/70 shadow-soft">
      <Table>
        <TableHeader>
          <TableRow className="bg-foreground/[0.02]">
            <TableHead className="px-5 py-4">Kết nối</TableHead>
            <TableHead>Loại</TableHead>
            <TableHead>Trạng thái</TableHead>
            <TableHead>Calls</TableHead>
            <TableHead>Cost</TableHead>
            <TableHead>Success</TableHead>
            <TableHead>Last used</TableHead>
            <TableHead className="px-5 text-right">Tác vụ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {integrations.map((integration) => (
            <TableRow key={integration.id}>
              <TableCell className="px-5 py-4">
                <div>
                  <p className="font-semibold">{integration.name}</p>
                  <p className="text-xs text-muted-foreground">{integration.provider}</p>
                </div>
              </TableCell>
              <TableCell>{integration.type}</TableCell>
              <TableCell>
                <StatusPill status={integration.status} />
              </TableCell>
              <TableCell>{new Intl.NumberFormat("en-US").format(integration.metrics.calls)}</TableCell>
              <TableCell className="font-medium text-indigo-600 dark:text-indigo-400">{formatCurrency(integration.metrics.cost)}</TableCell>
              <TableCell>{integration.metrics.successRate.toFixed(1)}%</TableCell>
              <TableCell>{integration.metrics.lastUsed}</TableCell>
              <TableCell className="px-5">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => refreshIntegration(integration)}>
                    Làm mới
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => renewKey(integration)}>
                    Đổi khóa
                  </Button>
                  {integration.status !== "connected" ? (
                    <Button size="sm" className="rounded-full" onClick={() => resolveIssue(integration)}>
                      {integration.status === "expired" ? "Kết nối lại" : "Xử lý"}
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-destructive hover:text-destructive"
                    onClick={() => disconnectIntegration(integration)}
                  >
                    Ngắt
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function StatusPill({ status }: { status: Integration["status"] }) {
  const config =
    status === "connected"
      ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : status === "warning"
        ? "border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400"
        : "border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400";

  return (
    <Badge variant="outline" className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${config}`}>
      {status}
    </Badge>
  );
}

function SummaryCard({
  title,
  value,
  description,
  icon,
  emphasize = "default",
}: {
  title: string;
  value: string;
  description: string;
  icon: React.ReactNode;
  emphasize?: "default" | "warning" | "danger";
}) {
  const className =
    emphasize === "warning"
      ? "border-amber-500/20 bg-amber-500/8"
      : emphasize === "danger"
        ? "border-red-500/20 bg-red-500/8"
        : "border-white/40 bg-card/72";

  return (
    <Card className={`rounded-[1.35rem] ${className} shadow-soft backdrop-blur-xl`}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{title}</CardTitle>
        <div className="rounded-full bg-background/70 p-2">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold md:text-3xl">{value}</div>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function StatusChip({
  label,
  active,
  tone = "default",
  onClick,
}: {
  label: string;
  active: boolean;
  tone?: "default" | "warning" | "danger";
  onClick: () => void;
}) {
  const toneClass =
    tone === "warning"
      ? active
        ? "border-amber-500 bg-amber-500 text-slate-950"
        : "border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400"
      : tone === "danger"
        ? active
          ? "border-red-500 bg-red-500 text-white"
          : "border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400"
        : active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card/65 text-foreground";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-2 text-xs font-medium transition-colors ${toneClass}`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function IntegrationSkeletonGrid() {
  return (
    <section className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={`summary-${index}`} className="rounded-[1.35rem] border-white/40 bg-card/72 backdrop-blur-xl">
            <CardHeader className="pb-3">
              <Skeleton className="h-3 w-28" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24" />
              <Skeleton className="mt-3 h-4 w-36" />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="rounded-[1.35rem] border border-white/40 bg-background/85 p-5 shadow-soft backdrop-blur-xl">
        <Skeleton className="h-11 w-full rounded-full" />
        <div className="mt-4 flex gap-2 overflow-hidden">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={`chip-${index}`} className="h-9 w-24 rounded-full" />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={`card-${index}`} className="min-h-[248px] rounded-[1.1rem] border-white/35 bg-card/80 p-5 shadow-soft">
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <Skeleton className="h-11 w-11 rounded-2xl" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((__, metricIndex) => (
                <div key={`metric-${metricIndex}`} className="rounded-2xl border border-border/70 p-3">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="mt-3 h-4 w-20" />
                </div>
              ))}
            </div>
            <Skeleton className="mt-4 h-14 w-full rounded-2xl" />
          </Card>
        ))}
      </div>
    </section>
  );
}
